import { Download16 } from '@carbon/icons-react';
import { Tooltip } from 'antd';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FileAttachment, FileAttachmentV2 } from 'api/attachments';
import {
  CpBody00Secondary,
  CpBody01Link,
  CpBody01Primary,
} from 'components/atoms/customer-portal/typography';
import { Loading } from 'components/atoms/loading';
import { FileExtensionIcon } from 'components/organisms/files/file-extension-icon';
import { useIsTablet } from 'hooks/use-media-query';
import { queueMacrotask } from 'utils/macrotask';
import { CpFilesEmptyState } from './cp-all-files-empty-state';

const Wrapper = styled.div``;

const File = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  padding-top: var(--cp-spacing-05);
  padding-bottom: var(--cp-spacing-05);
  border-bottom: 1px solid var(--cp-border-subtle);
  width: 100%;
  cursor: pointer;

  padding-left: var(--cp-spacing-05);

  &:hover {
    background: var(--cp-gray-10);
  }
`;

const Th = styled(File)`
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 0;
  height: 32px;
  background: var(--cp-coolest-gray-10);

  &:hover {
    background: var(--cp-coolest-gray-10);
    cursor: default;
  }
`;

const FileName = styled(CpBody01Primary)`
  margin-bottom: 0;
  /* ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  /* padding-left: var(--cp-spacing-07); */
  display: flex;
  align-items: center;
  justify-content: start;

  @media (max-width: 480px) {
    padding-left: var(--cp-spacing-03);
  }

  svg {
    margin-right: var(--cp-spacing-05);
  }
`;

const Source = styled(CpBody01Link)`
  margin-bottom: 0;
  /* ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 90%;
  padding-left: var(--cp-spacing-07);
  padding-right: var(--cp-spacing-07);

  @media (max-width: 768px) {
    display: none;
  }
`;

const Name = styled(CpBody00Secondary)`
  margin-bottom: 0;
  cursor: pointer;
  width: 100%;
  padding-left: 3.25rem;
`;

const IconColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: var(--cp-spacing-07);

  @media (max-width: 768px) {
    display: none;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  cursor: pointer;
  visibility: hidden;
  border-radius: var(--border-radius-default);

  ${File}:hover & {
    visibility: visible;
    &:hover {
      background: var(--cp-gray-20);
      cursor: pointer;
    }
  }
`;

const EmptyIconWrapper = styled(IconWrapper)`
  height: 0;
`;

const isFileAttachmentV2 = (
  attachment: FileAttachment | FileAttachmentV2,
): attachment is FileAttachmentV2 => {
  return 'attachment' in attachment;
};

const Item: React.FC<{
  attachment: FileAttachment & { sourceName?: string };
  onDownload: (attachment: FileAttachment) => void;
  onFileClick: () => void;
}> = ({ attachment, onDownload, onFileClick }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const onDownloadClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setIsDownloading(true);
      queueMacrotask(() => onDownload(attachment));
      setTimeout(() => setIsDownloading(false), 2000);
    },
    [attachment, onDownload],
  );

  return (
    <File
      data-cy={`cp.files.list-view.file.${attachment.name}`}
      onClick={onFileClick}
    >
      <FileName data-cy={`cp.files.list-view.file.${attachment.name}.name`}>
        <FileExtensionIcon name={attachment.name} height={20} width={20} />
        {attachment.name}
      </FileName>
      {attachment.sourceName ? <Source>{attachment.sourceName}</Source> : <></>}
      <IconColumn
        data-cy={`cp.files.list-view.file.${attachment.name}.download`}
      >
        <Tooltip title={<FormattedMessage id="download" />}>
          <IconWrapper onClick={onDownloadClick}>
            {isDownloading ? <Loading /> : <Download16 />}
          </IconWrapper>
        </Tooltip>
      </IconColumn>
    </File>
  );
};

export const CpFilesListView: React.FC<{
  attachments: (FileAttachment | FileAttachmentV2)[];
  onDownload: (attachment: FileAttachment) => void;
  onFileClick: (attachment: FileAttachment) => void;
}> = ({ attachments, onDownload, onFileClick }) => {
  const isResponsive = useIsTablet();

  return attachments?.length ? (
    <Wrapper data-cy="cp.files.list-view">
      {!isResponsive ? (
        <>
          <Th>
            <Name>
              <FormattedMessage id="name" />
            </Name>
            {/* <Name>
              <FormattedMessage id="source" />
            </Name> */}
            <IconColumn>
              <EmptyIconWrapper />
            </IconColumn>
          </Th>
        </>
      ) : (
        <></>
      )}
      {attachments.map((a) => {
        const isV2 = isFileAttachmentV2(a);

        if (isV2) {
          return (
            <Item
              onFileClick={() => onFileClick(a?.attachment)}
              key={a?.attachment?.attachmentId}
              attachment={{
                ...(a?.attachment ?? {}),
                sourceName: a?.source,
              }}
              onDownload={onDownload}
            />
          );
        }

        return (
          <Item
            key={a.attachmentId}
            attachment={a}
            onDownload={onDownload}
            onFileClick={() => onFileClick(a)}
          />
        );
      })}
    </Wrapper>
  ) : (
    <CpFilesEmptyState />
  );
};
